import { useState } from 'react';
import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import { useTheme } from '../../../../../../hooks/useTheme';
import { BREEDS } from '../breeds';
import { usePetForm, PET_NAME_MAX_LENGTH } from '../usePetForm';
import { FETCH_WELCOME_URL, PET_AGE_OPTIONS } from '../constants';
import styles from '../Fetch.module.css';
import d from '../FetchDesign.module.css';
import { useFetchDesign } from '../fetchDesign';
import { FetchSpeciesToggle } from './FetchSpeciesToggle';
import { FetchBreedCombobox } from './FetchBreedCombobox';
import type { FetchPetFormValues } from '../types';

interface FetchPetScreenMobileProps {
  onSubmit: (values: FetchPetFormValues) => void;
  loading?: boolean;
  initialEmail?: string;
  initialZip?: string;
  initialValues?: FetchPetFormValues | null;
}

const BENEFITS = [
  'Covers up to 90% of unexpected vet bills',
  'Use any vet in the U.S. or Canada',
  'Customize your price to fit your budget',
];

const PRIVACY_URL = 'https://www.fetchpet.com/privacy-policy';
const TERMS_URL = 'https://www.fetchpet.com/terms-conditions';

function ChevronDown() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

export function FetchPetScreenMobile({
  onSubmit,
  loading,
  initialEmail,
  initialZip,
  initialValues,
}: FetchPetScreenMobileProps) {
  const theme = useTheme();
  const { hero, heading } = useFetchDesign();
  const bodyFont = theme.bodytextDefaultNone?.fontFamily;
  const headerFont = theme.headersMediumNone?.fontFamily;
  const [benefitsOpen, setBenefitsOpen] = useState(false);
  const f = usePetForm({ loading, initialEmail, initialZip, initialValues });

  return (
    <form
      className={styles.petMobile}
      style={{ fontFamily: bodyFont }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!f.canSubmit) return;
        onSubmit(f.getValues());
      }}
    >
      <div className={styles.petMobileHeader}>
        <span
          className={styles.petMobileLogo}
          role="img"
          aria-label="Fetch Pet Insurance"
        />
        <span
          className={`${styles.petMobileHero} ${d.art}`}
          data-hero={hero}
          aria-hidden
        />
      </div>

      <div className={styles.petMobileScroll}>
        <div className={styles.petMobileIntro}>
          <h2
            className={styles.petMobileHeading}
            style={{ fontFamily: headerFont }}
          >
            {heading}
          </h2>
          <button
            type="button"
            className={styles.seeBenefits}
            aria-expanded={benefitsOpen}
            onClick={() => setBenefitsOpen((open) => !open)}
          >
            <span className={styles.seeBenefitsLabel}>See benefits</span>
            <span
              className={styles.seeBenefitsChevron}
              data-open={benefitsOpen ? 'true' : 'false'}
              aria-hidden
            >
              <ChevronDown />
            </span>
          </button>
          {benefitsOpen && (
            <>
              <ul className={styles.benefits}>
                {BENEFITS.map((benefit) => (
                  <li key={benefit} className={styles.benefit}>
                    <span className={styles.benefitCheck} aria-hidden>
                      ✓
                    </span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <a
                className={styles.coverageLink}
                href={FETCH_WELCOME_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                See what&apos;s covered
              </a>
            </>
          )}
        </div>

        <div className={styles.petMobileForm}>
          <h3
            className={styles.petMobileFormHeading}
            style={{ fontFamily: headerFont }}
          >
            Tell us about your pet
          </h3>

          <FetchSpeciesToggle value={f.species} onChange={f.setSpecies} />

          <label className={styles.fieldBox}>
            <span className={styles.fieldLabel}>Pet&apos;s name</span>
            <input
              className={styles.fieldInput}
              maxLength={PET_NAME_MAX_LENGTH}
              value={f.petName}
              onChange={(e) => f.setPetName(e.target.value)}
              placeholder="e.g. Taro"
            />
          </label>

          <FetchBreedCombobox
            breeds={BREEDS[f.species]}
            value={f.breedId}
            onChange={f.setBreedId}
          />

          <div className={styles.fieldRow}>
            <label className={styles.fieldBox}>
              <span className={styles.fieldLabel}>How old?</span>
              <select
                className={`${styles.fieldSelect} ${f.age === '' ? styles.fieldSelectPlaceholder : ''}`}
                value={f.age}
                onChange={(e) => f.setAge(e.target.value)}
              >
                <option value="" disabled>
                  Select an age
                </option>
                {PET_AGE_OPTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.fieldBox}>
              <span className={styles.fieldLabel}>ZIP code</span>
              <input
                className={styles.fieldInput}
                inputMode="numeric"
                maxLength={5}
                value={f.zip}
                onChange={(e) =>
                  f.setZip(e.target.value.replace(/\D/g, '').slice(0, 5))
                }
                placeholder="90210"
              />
            </label>
          </div>

          <label className={styles.fieldBox}>
            <span className={styles.fieldLabel}>Email</span>
            <input
              className={styles.fieldInput}
              type="email"
              value={f.emailDisplay}
              onFocus={f.beginEmailEdit}
              onBlur={f.endEmailEdit}
              onChange={(e) => f.setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>

          <div className={styles.legalRow}>
            <a
              className={styles.legalLink}
              href={PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            <span className={styles.legalDivider} aria-hidden />
            <a
              className={styles.legalLink}
              href={TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms &amp; Condition
            </a>
          </div>
        </div>
      </div>

      <div className={styles.petMobileFooter}>
        <Button type={ButtonType.submit} big disabled={!f.canSubmit}>
          {loading ? 'Getting your price…' : 'See my price'}
        </Button>
      </div>
    </form>
  );
}
